import React, { useEffect, useState } from 'react';
import { ArcAlert, ArcIconButton, ArcStack, ArcText, ArcToolbar } from '@src/core/ui';
import { useNavigate, useParams } from 'react-router-dom';
import { Actions, DetailsInfo } from './components';
import { useRequestAuthMutation } from '../../redux';
import { MissionIdParams } from '@src/core/entities';
import { useConfirmOrderBack } from '@src/features/order/hooks';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { routesConfig } from '@src/core/routes';


const getMessageLines = (message: string): { firstLine: string; secondLine: string | null } => {
  const parts = message.split(/\.\s+/).filter(Boolean);
  const sentences = parts.map((p, i) => (i < parts.length - 1 ? `${p}.` : p));
  const hasThird = sentences.length >= 3;
  return {
    firstLine: hasThird ? sentences.slice(0, 2).join(' ') : message,
    secondLine: hasThird ? sentences.slice(2).join(' ') : null,
  };
};

const WarningAlert: React.FC<{ message: string }> = ({ message }) => {
  const { firstLine, secondLine } = getMessageLines(message);
  return (
    <ArcAlert severity='error' icon={<WarningAmberIcon />} sx={{ bgcolor: 'error.background', py: 0.2, px: 1, '& .MuiAlert-icon': { marginLeft: '3px' } }}>
      <ArcStack spacing={0.001}>
        <ArcText.caption color='error.main'>{firstLine}</ArcText.caption>
        {secondLine != null && <ArcText.caption color='error.main'>{secondLine}</ArcText.caption>}
      </ArcStack>
    </ArcAlert>
  );
};

const ConfirmOrderSidebar: React.FC = () => {

  const [requestAuth] = useRequestAuthMutation();
  const { missionId } = useParams<MissionIdParams>();
  const [messages, setMessages] = useState<string[]>([]);
  const goBack = useConfirmOrderBack(missionId);
  const navigate = useNavigate();

  const onRequestAuth = React.useCallback(() => {
    if (!missionId) return;
    requestAuth(missionId).unwrap().then((res) => {
      setMessages(res?.warnings ?? []);
    });
  }, [missionId, requestAuth]);

  useEffect(() => {
    onRequestAuth();
  }, [onRequestAuth]);

  const onClose = () => navigate(routesConfig.orders, { replace: true });


  return (
    <ArcStack height='100%' minHeight={0} spacing={1} sx={{ overflow: 'hidden' }}>
      <ArcStack direction='row' justifyContent='space-between' alignItems='center' sx={{ pr: 0.5 }}>
        <ArcText.h4 fontSize={16} fontWeight={500} color='common.white'>
            CREATE MISSION
        </ArcText.h4>
        <ArcIconButton size='small' onClick={onClose} aria-label='Close' sx={{ p: 0 }}>
          <CloseIcon sx={{ color: 'common.white', p: 0 }} />
        </ArcIconButton>
      </ArcStack>

      <ArcStack flex={1} minHeight={0} spacing={1} sx={{ overflowY: 'auto', pr: 0.5 }}>
        <ArcStack>
          <ArcText.h5 marginTop={1} marginBottom={1} flexShrink={0} fontSize={16} fontWeight={500} letterSpacing={0.4} color='common.white'>
            2. Mission Summary
          </ArcText.h5>
        </ArcStack>

        <ArcStack spacing={1}>
          {messages.map((message) => (
            <WarningAlert key={message} message={message} />
          ))}
        </ArcStack>
        <DetailsInfo />

        <ArcStack sx={{pt: 1}}>
          <Actions isWarnings={messages.length > 0} />
        </ArcStack>

      </ArcStack>
    </ArcStack>
  );
};

export default ConfirmOrderSidebar;